import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Button, Icon, Loader } from 'semantic-ui-react'
import { useAuth, useChat } from '../../../hooks'
import { getPermissions } from '../Permissions'
import { MensajeChat } from './MensajeChat'
import './ChatDrawer.scss'

const SUGERENCIAS = [
    { permiso: 'view_beneficiario', texto: 'Buscá al beneficiario Orion con DNI ' },
    { permiso: 'view_servicio', texto: '¿Qué derivaciones en tratamiento tiene el DNI ' },
    { permiso: 'view_clientes', texto: '¿Está vigente la cobertura del DNI ' },
    { permiso: 'view_clientes', texto: 'Buscá al beneficiario Lizen de apellido ' },
    { permiso: 'view_lizenservicio', texto: '¿Qué servicios Lizen tiene facturados el DNI ' },
    { permiso: 'view_tiposervicio', texto: '¿Qué tipos de servicio Lizen hay vigentes?' }
]

/**
 * Si algo falla al renderizar el chat, se oculta el panel en vez de tirar abajo toda la app.
 */
class ChatErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = { fallo: false }
    }

    static getDerivedStateFromError() {
        return { fallo: true }
    }

    componentDidCatch(error, info) {
        console.error('Error en el asistente de consultas:', error, info)
    }

    render() {
        if (this.state.fallo) {
            return (
                <div className="chat-drawer chat-drawer--abierto chat-drawer--fallo">
                    <header className="chat-drawer__header">
                        <div className="chat-drawer__titulo">
                            <Icon name='warning sign' />
                            <span>Asistente de consultas</span>
                        </div>
                    </header>
                    <div className="chat-drawer__cuerpo chat-drawer__vacio">
                        <p>El asistente tuvo un problema al mostrar la conversación.</p>
                        <Button
                            basic
                            size='tiny'
                            content='Limpiar conversación y reintentar'
                            onClick={() => {
                                this.props.onReset()
                                this.setState({ fallo: false })
                            }}
                        />
                    </div>
                </div>
            )
        }
        return this.props.children
    }
}

/**
 * Boton flotante + panel lateral del asistente de consultas.
 * Se monta dentro de AdminLayout (tiene router) y solo se ve con el permiso use_chatbot.
 */
export function ChatDrawer() {
    const { limpiar } = useChat()
    return (
        <ChatErrorBoundary onReset={limpiar}>
            <ChatDrawerContenido />
        </ChatErrorBoundary>
    )
}

function ChatDrawerContenido() {
    const { auth } = useAuth()
    const { abierto, cargando, pendiente, mensajes, toggle, cerrar, limpiar, enviar, reintentar } = useChat()
    const history = useHistory()
    const location = useLocation()
    const [texto, setTexto] = useState('')
    const finRef = useRef(null)
    const inputRef = useRef(null)

    const habilitado = Boolean(auth?.me) && getPermissions('use_chatbot', auth)

    useEffect(() => {
        if (abierto && finRef.current) {
            finRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
        }
    }, [mensajes, cargando, abierto])

    useEffect(() => {
        if (abierto && inputRef.current) {
            inputRef.current.focus()
        }
    }, [abierto])

    if (!habilitado) return null

    const enviarTexto = () => {
        if (!texto.trim() || cargando) return
        enviar(texto, location.pathname)
        setTexto('')
    }

    const onKeyDown = (evento) => {
        if (evento.key === 'Enter' && !evento.shiftKey) {
            evento.preventDefault()
            enviarTexto()
        }
    }

    const navegar = (ruta) => {
        history.push(ruta)
        if (window.innerWidth <= 768) cerrar()
    }

    const sugerencias = SUGERENCIAS.filter(s => getPermissions(s.permiso, auth))

    return (
        <>
            <button
                type="button"
                className={`chat-boton ${abierto ? 'chat-boton--abierto' : ''}`}
                onClick={toggle}
                aria-label={abierto ? 'Cerrar asistente' : 'Abrir asistente de consultas'}
                title="Asistente de consultas"
            >
                <Icon name={abierto ? 'close' : 'comments'} />
                {pendiente && !abierto && <span className="chat-boton__punto" />}
            </button>

            <aside className={`chat-drawer ${abierto ? 'chat-drawer--abierto' : ''}`} aria-hidden={!abierto}>
                <header className="chat-drawer__header">
                    <div className="chat-drawer__titulo">
                        <Icon name='comments outline' />
                        <span>Asistente de consultas</span>
                    </div>
                    <div className="chat-drawer__acciones">
                        <button
                            type="button"
                            onClick={limpiar}
                            disabled={cargando || mensajes.length === 0}
                            title="Limpiar conversación"
                            aria-label="Limpiar conversación"
                        >
                            <Icon name='trash alternate outline' />
                        </button>
                        <button type="button" onClick={cerrar} title="Cerrar" aria-label="Cerrar">
                            <Icon name='close' />
                        </button>
                    </div>
                </header>

                {/* Scroll nativo a proposito: SimpleBar (data-simplebar) mueve los nodos por fuera de
                    React y rompe el render cuando la lista de mensajes cambia. */}
                <div className="chat-drawer__cuerpo">
                    {mensajes.length === 0 && (
                        <div className="chat-drawer__vacio">
                            <p>
                                Preguntame por beneficiarios, coberturas, derivaciones o servicios.
                                Sólo consulto datos: no modifico nada.
                            </p>
                            {sugerencias.length > 0 && (
                                <div className="chat-drawer__sugerencias">
                                    {sugerencias.map(s => (
                                        <button
                                            type="button"
                                            key={s.texto}
                                            className="chat-drawer__chip"
                                            onClick={() => {
                                                setTexto(s.texto)
                                                if (inputRef.current) inputRef.current.focus()
                                            }}
                                        >
                                            {s.texto.trim()}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {mensajes.map(mensaje => (
                        <MensajeChat
                            key={mensaje.id}
                            mensaje={mensaje}
                            onNavegar={navegar}
                            onReintentar={reintentar}
                            cargando={cargando}
                        />
                    ))}

                    {cargando && (
                        <div className="chat-mensaje chat-mensaje--asistente">
                            <div className="chat-mensaje__burbuja chat-mensaje__burbuja--cargando">
                                <Loader active inline size='tiny' />
                                <span>Consultando…</span>
                            </div>
                        </div>
                    )}
                    <div ref={finRef} />
                </div>

                <footer className="chat-drawer__footer">
                    <textarea
                        ref={inputRef}
                        rows={2}
                        value={texto}
                        placeholder="Escribí tu consulta… (Enter envía, Shift+Enter salto de línea)"
                        onChange={(e) => setTexto(e.target.value)}
                        onKeyDown={onKeyDown}
                        disabled={cargando}
                        maxLength={4000}
                    />
                    <Button
                        primary
                        icon='send'
                        onClick={enviarTexto}
                        disabled={cargando || !texto.trim()}
                        loading={cargando}
                        aria-label="Enviar"
                        title="Enviar"
                    />
                </footer>
            </aside>
        </>
    )
}
